/**
 * Formatação do protocolo de solicitação do Licenciamento Urbano para EXIBIÇÃO.
 *
 * O backend passou a devolver o protocolo novo SEM hífen (`"8K3M0626"`); inserir
 * o separador na renderização é responsabilidade do frontend (PR !710,
 * migration V60). O valor oficial continua sendo o que a API manda — o hífen é
 * só apresentação, e nunca deve ser enviado de volta nem usado em comparação.
 *
 * Convivem TRÊS formatos na base, e só o mais novo leva hífen:
 *
 * | Formato                  | Exemplo          | Formata? |
 * |--------------------------|------------------|----------|
 * | Novo (Base32 + MMAA)     | `8K3M0626`       | sim → `8K3M-0626` |
 * | Numérico de 14 dígitos   | `26090845296001` | não      |
 * | Legado alfanumérico      | `ALV2026000123`  | não      |
 *
 * Por isso a checagem é por PADRÃO, não por "tem 8 caracteres": aplicar o hífen
 * cegamente quebraria os dois formatos antigos, que nunca o tiveram.
 */

/**
 * Protocolo no formato novo: 4 caracteres Base32 (maiúsculos ou dígitos)
 * seguidos de 4 dígitos de mês/ano. Comprimento fixo de 8.
 *
 * Os dois formatos legados não casam aqui: o numérico tem 14 caracteres e o
 * alfanumérico tem 13.
 */
const FORMATO_NOVO = /^[0-9A-Z]{4}\d{4}$/;

/** true quando o valor está no formato novo, que é o único que recebe hífen. */
export const ehProtocoloNovo = (
  protocolo: string | null | undefined
): boolean => FORMATO_NOVO.test((protocolo ?? "").trim());

/**
 * Devolve o protocolo pronto para a tela.
 *
 * - formato novo  → insere o hífen (`8K3M0626` → `8K3M-0626`)
 * - demais valores → devolve exatamente como veio, inclusive vazio e nulo
 *
 * Nunca lança: recebe o que a API mandar.
 */
export const formatarProtocolo = (
  protocolo: string | null | undefined
): string => {
  const bruto = (protocolo ?? "").trim();

  if (!ehProtocoloNovo(bruto)) return bruto;

  return `${bruto.slice(0, 4)}-${bruto.slice(4)}`;
};

/**
 * Desfaz a apresentação: devolve o protocolo como o backend o guarda.
 *
 * O hífen existe só na tela, mas o cidadão copia o que está vendo e cola no
 * campo de protocolo vinculado (ou na busca). O backend casa o protocolo por
 * igualdade EXATA, então o hífen colado junto derrubaria o vínculo. Nenhum dos
 * três formatos usa hífen no valor real — removê-lo é seguro para todos.
 */
export const desformatarProtocolo = (
  protocolo: string | null | undefined
): string => (protocolo ?? "").trim().replace(/-/g, "");

/**
 * Prepara um termo de busca livre que PODE ser um protocolo.
 *
 * As buscas do Licenciamento Urbano aceitam protocolo ou nome de equipe no
 * mesmo campo, e o backend compara o protocolo como texto cru. Quem copia
 * "8K3M-0626" da tela e cola aqui não acharia nada. Remover o hífen de
 * qualquer termo, porém, corromperia o nome de uma equipe que o tenha — por
 * isso o hífen só cai quando o que sobra é exatamente um protocolo do formato
 * novo; qualquer outro termo passa intacto.
 */
export const normalizarBuscaProtocolo = (
  termo: string | null | undefined
): string => {
  const bruto = (termo ?? "").trim();
  const semHifen = desformatarProtocolo(bruto);

  return ehProtocoloNovo(semHifen.toUpperCase()) ? semHifen : bruto;
};
